import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { useCondicoesPagamento } from '@/api/condicoes-pagamento';
import { useTabelaCondicoes } from '@/api/tabela-condicao';
import { useTabelasPreco } from '@/api/tabelas-preco';
import { SeletorCategoria } from '@/components/categorias/seletor-categoria';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UFS } from '@triade/contracts';
import { SeletorContribuinte } from '@/components/destino-tributario/seletor-contribuinte';
import { useDebounced } from '@/hooks/use-debounced';
import { cn } from '@/lib/utils';
import type { BuscaLista } from './tipos';

const TODAS = '__todas__';
/** Opcao "origem de cada tabela" do seletor de UF de destino (o Radix Select nao aceita vazio). */
const ORIGEM = '__origem__';
const PADRAO = '__padrao__';
const TODOS_TIPOS = '__todos__';

const BOTAO = 'h-[34px] rounded-[9px] border bg-card px-3 text-small hover:bg-line-soft';
const TRIGGER = 'h-[34px] min-w-[150px] rounded-[9px] bg-card text-small';
const ROTULO = 'text-caption font-medium text-ink-secondary';

export interface FiltrosListaProps {
  busca: BuscaLista;
  onBusca: (mudanca: Partial<Record<keyof BuscaLista, BuscaLista[keyof BuscaLista] | undefined>>) => void;
  /** Mostra o filtro "Sem ficha técnica" (so quem tem custo.ver; a API recusa o filtro aos demais). Padrao: sim. */
  comSemFicha?: boolean;
}

/** Busca, filtros (abaixo do sugerido, sem praticado, sem ficha, tipo) e seletores de tabela, condicao e data. Nada filtra no front. */
export function FiltrosLista({ busca, onBusca, comSemFicha = true }: FiltrosListaProps) {
  const [termo, setTermo] = useState(busca.q ?? '');
  const termoAtraso = useDebounced(termo.trim(), 300);
  useEffect(() => {
    if (termoAtraso !== (busca.q ?? '')) onBusca({ q: termoAtraso || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termoAtraso]);
  useEffect(() => setTermo(busca.q ?? ''), [busca.q]);

  const tabelas = useTabelasPreco({ ativa: true, limit: 200 });
  const vinculos = useTabelaCondicoes(busca.tabelaId);
  const condicoes = useCondicoesPagamento({ ativa: true, limit: 200 });
  const ligadas = new Set((vinculos.data ?? []).map((v) => v.condicaoId));
  const condicoesDaTabela = (condicoes.data ?? []).filter((c) => ligadas.has(c.id));

  const alternar = (campo: 'abaixo' | 'semPraticado' | 'semFicha') =>
    onBusca({ [campo]: busca[campo] ? undefined : true });
  const botao = (campo: 'abaixo' | 'semPraticado' | 'semFicha', rotulo: string) => (
    <button
      type="button"
      aria-pressed={Boolean(busca[campo])}
      onClick={() => alternar(campo)}
      className={cn(BOTAO, busca[campo] ? 'border-primary bg-card font-semibold text-primary' : 'border-border')}
    >
      {rotulo}
    </button>
  );

  return (
    <div className="flex flex-col gap-3 border-b border-border px-4 py-3.5">
      <p className="text-caption text-ink-muted">
        UF e contribuinte mudam só o sugerido das tabelas estruturadas (leitura); o preço praticado é único e não muda.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="grid gap-1.5">
          <label htmlFor="lp-tabela" className={ROTULO}>
            Tabela de preço
          </label>
          <Select
            value={busca.tabelaId ?? TODAS}
            onValueChange={(v) => onBusca({ tabelaId: v === TODAS ? undefined : v, condicaoId: undefined })}
          >
            <SelectTrigger id="lp-tabela" className={TRIGGER}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODAS}>Todas as tabelas</SelectItem>
              {(tabelas.data ?? []).map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <label htmlFor="lp-uf" className={ROTULO}>
            UF de destino
          </label>
          <Select value={busca.ufDestino ?? ORIGEM} onValueChange={(v) => onBusca({ ufDestino: v === ORIGEM ? undefined : (v as BuscaLista['ufDestino']) })}>
            <SelectTrigger id="lp-uf" className={cn(TRIGGER, 'min-w-[130px]')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ORIGEM}>Origem da tabela</SelectItem>
              {UFS.map((uf) => (
                <SelectItem key={uf} value={uf}>
                  {uf}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <label htmlFor="lp-contribuinte" className={ROTULO}>
            Contribuinte de ICMS
          </label>
          <SeletorContribuinte
            id="lp-contribuinte"
            valor={busca.contribuinte !== false}
            onChange={(v) => onBusca({ contribuinte: v ? undefined : false })}
            className="h-[34px] min-w-[190px] rounded-[9px] text-small"
          />
        </div>
        <div className="grid gap-1.5">
          <label htmlFor="lp-condicao" className={ROTULO}>
            Condição de pagamento
          </label>
          <Select
            value={busca.condicaoId ?? PADRAO}
            disabled={!busca.tabelaId}
            onValueChange={(v) => onBusca({ condicaoId: v === PADRAO ? undefined : v })}
          >
            <SelectTrigger id="lp-condicao" className={TRIGGER} title={busca.tabelaId ? undefined : 'Escolha uma tabela para escolher a condição'}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={PADRAO}>Condição padrão</SelectItem>
              {condicoesDaTabela.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <label htmlFor="lp-data" className={ROTULO}>
            Data de referência
          </label>
          <input
            id="lp-data"
            type="date"
            value={busca.data ?? ''}
            onChange={(e) => onBusca({ data: e.target.value || undefined })}
            className="h-[34px] rounded-[9px] border border-input bg-card px-2.5 text-small outline-none focus-visible:border-primary focus-visible:shadow-focus"
          />
        </div>
        <div className="grid gap-1.5">
          <label htmlFor="lp-tipo" className={ROTULO}>
            Tipo
          </label>
          <Select value={busca.tipo ?? TODOS_TIPOS} onValueChange={(v) => onBusca({ tipo: v === TODOS_TIPOS ? undefined : (v as BuscaLista['tipo']) })}>
            <SelectTrigger id="lp-tipo" className={cn(TRIGGER, 'min-w-[130px]')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS_TIPOS}>Todos os tipos</SelectItem>
              <SelectItem value="PRODUTO">Produto</SelectItem>
              <SelectItem value="MERCADORIA">Mercadoria</SelectItem>
              <SelectItem value="SERVICO">Serviço</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <SeletorCategoria
            id="lp-categoria"
            rotulo="Categoria"
            comTodas
            value={busca.categoriaId}
            onChange={(v) => onBusca({ categoriaId: v })}
            className="h-[34px] min-w-[150px] w-auto rounded-[9px] text-small"
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex h-[34px] w-[280px] max-w-full items-center gap-2 rounded-[9px] border border-input bg-card px-2.5 focus-within:border-primary focus-within:shadow-focus">
          <Search aria-hidden="true" className="size-[15px] text-ink-muted" />
          <label htmlFor="lp-q" className="sr-only">
            Buscar produto
          </label>
          <input
            id="lp-q"
            type="search"
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            placeholder="Código ou descrição"
            autoComplete="off"
            className="w-full min-w-0 bg-transparent text-small outline-none"
          />
        </div>
        {botao('abaixo', 'Praticado abaixo do sugerido')}
        {botao('semPraticado', 'Sem preço praticado')}
        {comSemFicha && botao('semFicha', 'Sem ficha técnica')}
      </div>
    </div>
  );
}
